import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { mdiCallSplit, mdiOpenInNew } from '@mdi/js'
import { formatPageRange } from './DocumentBundleContents'

interface DocumentChildBannerProps {
    org: string
    parentUid: string
    parentName?: string | null
    pages?: number[] | null
}

/**
 * DocumentChildBanner renders on a bundle child document: a banner linking back
 * to the parent bundle, showing the covered page range.
 */
export function DocumentChildBanner({ org, parentUid, parentName, pages }: DocumentChildBannerProps) {
    const { t } = useTranslation('documents')
    const navigate = useNavigate()

    const range = formatPageRange(pages)
    const name = parentName || ''

    const goToParent = () => {
        navigate({ to: '/$org/documents/$docUid', params: { org, docUid: parentUid } })
    }

    return (
        <div
            className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-center justify-between gap-2"
            data-testid="child-banner"
        >
            <div className="flex items-center gap-2 min-w-0 text-sm">
                <AppIcon path={mdiCallSplit} size="md" className="text-primary shrink-0" />
                <span className="truncate">
                    {t('bundle.part_of')}{' '}
                    <button
                        type="button"
                        onClick={goToParent}
                        className="text-primary hover:underline"
                        data-testid="child-banner-parent-name"
                        aria-label={name}
                        title={name}
                    >
                        {name}
                    </button>
                    {range && <span className="text-muted-foreground"> — {t('bundle.part_of_pages', { range })}</span>}
                </span>
            </div>
            <button
                onClick={goToParent}
                className="flex items-center gap-1 text-primary hover:underline shrink-0"
                data-testid="child-banner-parent-link"
            >
                <span>{t('bundle.view_parent')}</span>
                <AppIcon path={mdiOpenInNew} size="xs" />
            </button>
        </div>
    )
}
